import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";

import {
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
} from "@mui/material";

import { ClearLines } from "components/EditPage/ClearLines";
import { LineTableHeadingCell } from "components/formFields/LineTableCell";
import { LineColumnFillHeading } from "components/formFields/line/LineColumnFillHeading";
import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { LineDragInsertCopy } from "components/formFields/line/LineDragInsertCopy";
import { LineInventoryItemHeading } from "components/formFields/line/LineInventoryItem";
import { DragHandleHeading } from "components/utility/DragHandle";

import { i18n } from "services/i18nService";
import { expandItemGroup } from "services/sosInventoryService/domainLogic";
import { setPageDirty } from "services/utility/edit";
import { handleDrop, copyDown } from "services/utility/lineItems";

import { useJobWorkcenters } from "hooks/useJobWorkcenters";
import { useRelated } from "hooks/useRelated";
import { useWorkers } from "hooks/useWorkers";

import { LineItem } from "views/Builds/Build/Input/LineItem";
import { LineItemFooter } from "views/Builds/Build/Input/LineItemFooter";

import { EMPTY_INPUT_LINE_ITEM } from "editConfig";

export function LineItems(props) {
  const {
    record,
    objectType,
    output,
    lines,
    items,
    totals,
    bins,
    relatedRecords,
    itemsCount,
    addItem,
    lineHandler,
    setClearLines,
    lineItemsErrors,
    setErrors,
  } = props;

  const classes = useRelated("class");
  const workers = useWorkers();
  const jobWorkcenters = useJobWorkcenters();

  // SETTINGS
  const classEnabled = useSelector(
    (state) => state.userCompanySettings.settings.classEnabled
  );
  const showQuantityOnBuildsAndShipments = useSelector(
    (state) =>
      state.userCompanySettings.settings.showQuantityOnBuildsAndShipments
  );
  const jobEnabled = useSelector(
    (state) => state.userCompanySettings.settings.jobEnabled
  );
  const uomEnabled = useSelector(
    (state) => state.userCompanySettings.settings.uomEnabled
  );

  function insertEmptyLine(insertAt) {
    setPageDirty();
    lineHandler({
      type: "insert",
      insertAt: insertAt,
      newLine: EMPTY_INPUT_LINE_ITEM[objectType],
    });
  }

  if (!lines) {
    return null;
  }

  return (
    <TableContainer data-testing="inputLineItems" sx={{ overflowX: "initial" }}>
      <Table size="small" padding="none" stickyHeader>
        <TableHead>
          <TableRow sx={{ verticalAlign: "bottom", lineHeight: "1" }}>
            <DragHandleHeading />
            <ClearLines setClearLines={setClearLines} />
            <LineTableHeadingCell>{i18n("columns.Line")}</LineTableHeadingCell>
            <LineInventoryItemHeading lineHandler={lineHandler} />
            <LineColumnHeading labelCode="Description" />
            {showQuantityOnBuildsAndShipments && (
              <LineColumnHeading labelCode="OnHand" align="right" />
            )}
            <LineColumnHeading labelCode="UsedPer" align="right" />
            <LineColumnHeading labelCode="TotalInBuild" />
            {uomEnabled && <LineColumnHeading labelCode="UOM" />}
            <LineColumnHeading labelCode="Notes" />
            {classEnabled && (
              <LineColumnFillHeading
                labelCode="Class"
                field="class"
                lineHandler={lineHandler}
              />
            )}
            {jobEnabled && <LineColumnHeading labelCode="FromJob" />}
          </TableRow>
        </TableHead>
        <DragDropContext
          onDragEnd={(result) => handleDrop(result, lineHandler, lines)}
        >
          <Droppable droppableId="lineItems">
            {(provided) => (
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {lines.map((line, index) => (
                  <Draggable key={index} draggableId={`${index}`} index={index}>
                    {(draggableProvided, snapshot) => {
                      return (
                        <TableRow
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          sx={{
                            ...draggableProvided.draggableProps.style,
                            position: "inherit",
                            backgroundColor: snapshot.isDragging
                              ? "dragBackground"
                              : "none",
                          }}
                          data-testing={"line-" + index}
                        >
                          <LineDragInsertCopy
                            draggableProvided={draggableProvided}
                            snapshot={snapshot}
                            insertEmptyLine={insertEmptyLine}
                            lineNumber={line.lineNumber}
                            onCopyDown={() =>
                              copyDown(line, lineHandler, objectType)
                            }
                            showCopyDown={
                              line.lineNumber === lines.length ? false : true
                            }
                          />

                          <LineItem
                            record={record}
                            objectType={objectType}
                            output={output}
                            bins={bins}
                            workers={workers}
                            classes={classes}
                            relatedRecords={relatedRecords}
                            line={line}
                            lineHandler={lineHandler}
                            expandItemGroup={(line) =>
                              expandItemGroup(
                                line,
                                lineHandler,
                                objectType,
                                record
                              )
                            }
                            items={items}
                            itemsCount={itemsCount}
                            addItem={addItem}
                            jobWorkcenters={jobWorkcenters}
                            lineItemsErrors={lineItemsErrors}
                            setErrors={setErrors}
                            draggableProvided={draggableProvided}
                            draggableSnapshot={snapshot}
                          />
                        </TableRow>
                      );
                    }}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
        <LineItemFooter
          totals={totals}
          lineHandler={lineHandler}
          objectType={objectType}
        />
      </Table>
    </TableContainer>
  );
}
